import { firebase, auth } from './firebase-app';
import { firebaseAppCredentials } from '../config/firebase';
import { dataProvider } from './data';
import { fetch2faSetup, verify2faCode } from '../helpers/web-api';

// ─────────────────────────────────────────────────────────────────────────────
// A-3.a (ronda 71) — AuthProvider base DE LA CASA (reemplaza al de
// react-admin-firebase). Expone exactamente la superficie que el wrapper de
// 2FA de abajo y web-api consumen: login/logout/checkError/getPermissions +
// getAuthUser() (espera la restauración de sesión del reload: onAuthStateChanged
// emite el estado actual al suscribirse) + getJWTToken() (el ID token con el
// que el BO se autentica contra los endpoints admin de la web). El wrapper de
// 2FA quedó INTACTO: mismo flujo en dos fases, mismo claim boMfa, mismo
// canje del custom token.
// ─────────────────────────────────────────────────────────────────────────────

function getAuthUser(): Promise<firebase.User> {
  return new Promise((resolve, reject) => {
    const off = auth.onAuthStateChanged(
      (user) => {
        off();
        if (user) resolve(user);
        else reject(new Error('sin sesión'));
      },
      (err) => {
        off();
        reject(err);
      },
    );
  });
}

let baseAuthProvider = {
  login: async ({ username, password }: any) => {
    await auth.signInWithEmailAndPassword(String(username ?? ''), String(password ?? ''));
  },
  logout: async () => {
    await auth.signOut().catch(() => undefined);
  },
  checkError: async (error: any) => {
    const status = error?.status ?? error?.response?.status;
    if (status === 401 || status === 403) {
      throw new Error('unauthorized');
    }
  },
  checkAuth: async () => {
    await getAuthUser();
  },
  getPermissions: async (_params?: any) => {
    try {
      const user = await getAuthUser();
      const token = await user.getIdTokenResult();
      return token?.claims ?? {};
    } catch {
      return {};
    }
  },
  getAuthUser,
  getJWTToken: async (): Promise<string> => {
    const user = await getAuthUser();
    // getIdToken(TRUE) = forzar refresh. Sin esto, el ID token cacheado dura
    // 1 h y puede perder el claim `boMfa` (que la web exige en requireAdminToken
    // y las reglas de Firestore exigen para escribir). Era la causa de que, tras
    // un rato de sesión, los mails disparados desde el BO (reset, suspensión,
    // reactivación, aviso de aprobación/rechazo de tx) fallaran con 401 y de que
    // crear interés cayera con "Missing or insufficient permissions". El swap del
    // cliente no lo sufría: va por la sesión de la web, no por este token.
    return user.getIdToken(true);
  },
};

// Único admin habilitado para la consola. Es una allowlist del lado del cliente
// (defensa en profundidad + UX): el candado REAL vive en las reglas de Firestore
// y en el punto donde la web acuña el claim `boMfa` (sólo para este email). Debe
// coincidir con ADMIN_EMAIL de la web (src/lib/admin-config.ts).
const ADMIN_EMAIL = 'damian.prato@yieldbox.me';

let createUserFirebaseApp: any = null;

try {
  createUserFirebaseApp = firebase.initializeApp(
    firebaseAppCredentials,
    'createUserOnly'
  );
} catch (err) {
  createUserFirebaseApp = firebase.app('createUserOnly');
}

export let authProvider = {
  ...baseAuthProvider,
  checkError: async (params: any) => {
    return await baseAuthProvider.checkError(params);
  },
  checkAuth: async (params: any) => {
    // 1) Tiene que haber sesión de Firebase Auth.
    let authUser: any = await baseAuthProvider.getAuthUser().catch(() => null);
    if (!authUser) {
      return Promise.reject({ redirectTo: '/login' });
    }

    // 2) La sesión tiene que haber pasado 2FA: el ID token debe llevar el claim
    //    `boMfa`. Ese claim sólo se consigue canjeando el custom token que emite
    //    la web tras verificar el código (ver completeTwoFactor). Una sesión
    //    abierta sólo con contraseña NO lo tiene → se la echa. Es el mismo muro
    //    que aplican las reglas de Firestore; acá se chequea antes para
    //    redirigir prolijo en vez de reventar en un error de permisos.
    try {
      let tokenRes = await authUser.getIdTokenResult();
      if (tokenRes?.claims?.boMfa !== true) {
        await firebase.auth().signOut().catch(() => undefined);
        return Promise.reject({ redirectTo: '/login' });
      }
    } catch (e) {
      await firebase.auth().signOut().catch(() => undefined);
      return Promise.reject({ redirectTo: '/login' });
    }

    // 3) Y, además, tiene que ser el ADMIN único (email allowlist) y admin en
    //    Firestore. Defensa en profundidad SOBRE las reglas. Si no, se cierra la
    //    sesión para no dejar a un no-admin con sesión viva apuntando al BO.
    try {
      let emailOk =
        String(authUser.email ?? '').trim().toLowerCase() === ADMIN_EMAIL;
      let { data: user } = await dataProvider.getOne('users', {
        id: authUser.uid,
      });
      if (user?.isAdmin === true && emailOk) {
        return Promise.resolve();
      }
    } catch (e) {
      // Doc inexistente o lectura denegada → no autorizado.
    }

    await firebase.auth().signOut().catch(() => undefined);
    return Promise.reject({ redirectTo: '/login' });
  },
  getPermissions: async (params: any) => {
    return await baseAuthProvider.getPermissions(params);
  },
  getIdentity: async () => {
    let { uid } = await baseAuthProvider.getAuthUser();

    let { data: user } = await dataProvider.getOne('users', {
      id: uid,
    });

    return {
      id: uid,
      avatar: ``,
      fullName: `${user.fullName ?? ''}`,
      isAdmin: user.isAdmin ?? false,
    };
  },
  sendPasswordResetEmail: async ({ email }: any) => {
    await firebase.auth().sendPasswordResetEmail(email);
  },
  // ── 2FA (Google Authenticator) ──────────────────────────────────────────
  // El login del BO es en dos fases:
  //   (A) email + contraseña → deja una sesión "a medias" (con ID token, pero
  //       SIN el claim boMfa: si las reglas exigen boMfa, no puede tocar datos).
  //       Sólo sirve para hablar con los endpoints de 2FA de la web.
  //   (B) verificado el código, la web devuelve un custom token con boMfa y acá
  //       se canjea → sesión con acceso.
  // El secreto TOTP nunca baja al navegador: sólo viaja el QR.

  // Pide a la web el QR de enrolamiento (o el aviso de que ya está enrolado).
  // De paso valida server-side que quien se logueó es EL admin (el endpoint
  // exige isAdmin + email allowlist): si no lo es, tira 401/403.
  startTwoFactor: async () => {
    return await fetch2faSetup(baseAuthProvider);
  },
  // Verifica el código (TOTP o de recuperación) en la web y canjea el custom
  // token resultante. Devuelve { ok, recoveryCodes? } (los códigos, sólo en el
  // primer enrolamiento). Tras esto, la sesión lleva el claim boMfa.
  completeTwoFactor: async (code: string) => {
    let res = await verify2faCode(baseAuthProvider, code);
    if (!res?.ok || !res?.token) {
      throw new Error('2FA verification failed');
    }
    await firebase.auth().signInWithCustomToken(res.token);
    return res;
  },
  createUser: async (
    { email, password, fullName }: any,
    shouldSendVerificationEmail: boolean = true
  ) => {
    let { user } = await createUserFirebaseApp
      .auth()
      .createUserWithEmailAndPassword(email, password);

    await user?.updateProfile({
      displayName: fullName,
    });

    if (!user?.emailVerified && shouldSendVerificationEmail) {
      await user?.sendEmailVerification();
    }

    await createUserFirebaseApp.auth().signOut();

    return user;
  },
};
